<script setup>
/**
 * 「修改密码」页，逐条对齐旧版 web/37.5a11ec6870780acbea75.js 里的
 * app-password 组件（类 i、模板工厂 k + 子模板 m、样式 f）。
 *
 * 旧版表单（Angular FormGroup）：
 *   old_password:     ['', [required]]
 *   new_password:     ['', [required]]
 *   confirm_password: ['', [required]] + 自定义校验器 [equal]="password.value"
 *
 * 注意：新密码只有 required，**没有**长度校验。placeholder 写着「请输入密码，6位以上」
 * 但旧版并不在前端拦，长度由后端判断。这里照旧版办，不额外加 6 位校验。
 *
 * save():
 *   loading「正在提交，请稍等」
 *   changPassword(form.value) → POST /user/password {old_password,new_password,confirm_password}
 *   成功 → toast「修改密码成功」+ nav.navigateBack('/tabs/user')
 *   失败 → toast(error_msg)
 *
 * 模板：
 *   ion-header > ion-toolbar
 *     ion-buttons[slot=start] > ion-button[routerLink="/tabs/user"] > ion-icon[name=arrow-back]
 *     ion-title "修改密码"
 *   ion-content[padding]
 *     form > ion-list > 三个 ion-item（ion-label + ion-input[type=password][clearInput]）
 *            div.action > ion-button color="secondary" expand="full" [disabled]="!form.valid" "确 认"
 *            *ngIf="confirm_password.dirty && errors.equal" → ion-text[color=danger] > p.danger-tip「两次密码不一样」
 *
 * 样式 f 只有一条 .key{color:red}，且模板里没用到，所以这页没有自定义样式。
 */
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { request } from '../services/api'

const router = useRouter()

const oldPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
// 对应 Angular 的 dirty：用户动过这个控件才显示不一致提示
const confirmDirty = ref(false)
const submitting = ref(false)
const notice = ref('')

/** 旧版自定义校验器 equal：confirm_password 必须与 new_password 相同 */
const equalError = computed(() => confirmPassword.value !== newPassword.value)

/** 旧版 [disabled]="!form.valid"：三个必填 + equal 校验都过才可点 */
const formValid = computed(() =>
  Boolean(oldPassword.value) && Boolean(newPassword.value) && Boolean(confirmPassword.value) && !equalError.value)

/** 旧版 *ngIf="form.get('confirm_password').dirty && errors?.equal" */
const showEqualTip = computed(() => confirmDirty.value && equalError.value)

/** 旧版 save() */
async function save() {
  if (!formValid.value || submitting.value) return
  submitting.value = true
  notice.value = '正在提交，请稍等'
  try {
    await request('/user/password', {
      method: 'POST',
      data: {
        old_password: oldPassword.value,
        new_password: newPassword.value,
        confirm_password: confirmPassword.value,
      },
    })
    notice.value = '修改密码成功'
    // 旧版 nav.navigateBack('/tabs/user')，新版个人中心是 /user/profile
    window.setTimeout(() => router.replace('/user/profile'), 1200)
  } catch (error) {
    notice.value = error.message || '修改失败'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div class="password-page">
    <!-- 旧版 ion-header > ion-toolbar，返回按钮是 routerLink="/tabs/user"（不是历史后退） -->
    <header class="modal-header">
      <RouterLink class="modal-back" to="/user/profile" aria-label="返回">
        <svg class="modal-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" /></svg>
      </RouterLink>
      <h1 class="modal-title">修改密码</h1>
      <span class="modal-spacer" aria-hidden="true"></span>
    </header>

    <!-- 旧版 ion-content[padding] -->
    <div class="modal-content">
      <form novalidate @submit.prevent="save">
        <section class="ion-list">
          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">旧密码：</div>
              <div class="ion-input">
                <input v-model="oldPassword" class="native-input" type="password" placeholder="请输入旧密码" />
                <button v-if="oldPassword" class="input-clear-icon" type="button" aria-label="清除" @click="oldPassword = ''"></button>
              </div>
            </div>
          </div>

          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">新密码：</div>
              <div class="ion-input">
                <input v-model="newPassword" class="native-input" type="password" placeholder="请输入密码，6位以上" />
                <button v-if="newPassword" class="input-clear-icon" type="button" aria-label="清除" @click="newPassword = ''"></button>
              </div>
            </div>
          </div>

          <div class="list-item">
            <div class="list-item-inner">
              <div class="ion-label">确认密码：</div>
              <div class="ion-input">
                <input
                  v-model="confirmPassword"
                  class="native-input"
                  type="password"
                  placeholder="请再输入一次密码"
                  @input="confirmDirty = true"
                />
                <button v-if="confirmPassword" class="input-clear-icon" type="button" aria-label="清除" @click="confirmPassword = ''"></button>
              </div>
            </div>
          </div>
        </section>

        <!-- 旧版 div.action > ion-button color="secondary" expand="full"，文本「确 认」中间带空格 -->
        <div class="action">
          <button
            class="ion-button button-full button-solid ion-color-secondary"
            type="button"
            :class="{ 'button-disabled': !formValid || submitting }"
            :disabled="!formValid || submitting"
            @click="save"
          >
            <span class="button-inner">确 认</span>
          </button>
        </div>

        <!-- 旧版 m：ion-text[color=danger] > p.danger-tip -->
        <div v-if="showEqualTip" class="ion-text-danger">
          <p class="danger-tip"> 两次密码不一样 </p>
        </div>

        <p v-if="notice" class="modal-notice">{{ notice }}</p>
      </form>
    </div>
  </div>
</template>

<style scoped>
/* 页面外壳与顶栏跟其它旧版还原页保持一致 */
.password-page{position:fixed;z-index:1;top:0;left:0;width:100%;height:100%;background:#fff;color:#000;display:flex;flex-direction:column;font:14px Arial,"Microsoft YaHei",sans-serif}
.modal-header{display:flex;align-items:center;height:56px;background:var(--legacy-theme,#f95454);color:var(--legacy-toolbar-color,#fff);position:relative;z-index:1;box-shadow:0 2px 4px rgb(0 0 0 / 20%)}
.modal-back{display:flex;align-items:center;justify-content:center;width:56px;height:56px;border:0;background:transparent;line-height:1;color:inherit;cursor:pointer;padding:0;text-decoration:none}
.modal-back .modal-icon{width:24px;height:24px;display:block}
.modal-title{flex:1;margin:0;font-size:18px;font-weight:500;text-align:center;color:inherit}
.modal-spacer{width:56px;flex:none}
/* ion-content[padding] → 四边 16px */
.modal-content{flex:1;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:16px}

/* --- Ionic 4 md 的真实默认值，取自 web/135.773ae5d979ae5c36fbef.js --- */
.ion-list{display:block;margin:0;padding:8px 0;background:#fff;list-style-type:none}
.list-item{display:flex;align-items:center;justify-content:space-between;width:100%;min-height:48px;margin:0;padding:0 0 0 16px;background:#fff;color:#000;font-size:.8rem;overflow:hidden;position:relative;box-sizing:border-box}
/* space-between 对应 .item-native；ion-input 自带 flex:1，所以会紧跟在 label 后面 */
.list-item-inner{display:flex;flex:1;flex-direction:inherit;align-items:inherit;justify-content:space-between;align-self:stretch;min-height:inherit;padding:0 16px 0 0;border-width:0 0 1px 0;border-style:solid;border-color:rgba(0,0,0,.13);overflow:hidden;box-sizing:border-box}
/* ion-label（md）本身不带 flex，宽度按内容走 */
.ion-label{display:block;font-size:inherit;text-overflow:ellipsis;white-space:nowrap;overflow:hidden}

/* ion-input（md）。padding 取的是 Ionic 4 默认值，未从旧版打包产物逐条核对
   （ion-input 在 web/123 里，取样需要按偏移读单行大文件）。 */
.ion-input{display:flex;align-items:center;flex:1;position:relative;padding:10px 0 11px 8px;font-size:inherit}
.ion-input .native-input{flex:1;width:100%;margin:0;padding:0;border:0;outline:none;background:transparent;color:inherit;font:inherit;box-sizing:border-box}
.ion-input .native-input::placeholder{color:currentColor;opacity:.33}
.input-clear-icon{width:30px;height:30px;border:0;background:transparent;cursor:pointer;padding:0;position:relative;opacity:.5}
.input-clear-icon::before,.input-clear-icon::after{content:"";position:absolute;left:9px;top:14px;width:12px;height:1.5px;background:currentColor}
.input-clear-icon::before{transform:rotate(45deg)}
.input-clear-icon::after{transform:rotate(-45deg)}

/* ion-button color="secondary" expand="full"，取自 web/75（md）
   secondary → --ion-color-secondary #0cd1e8；disabled → opacity .5 */
.action{margin-top:8px}
.ion-button{
  display:flex;align-items:center;justify-content:center;
  width:100%;margin:4px 0;border:0;border-radius:0;
  height:36px;font-size:14px;font-weight:500;letter-spacing:.06em;text-transform:uppercase;cursor:pointer;
  background:var(--ion-color-secondary,#0cd1e8);color:#fff;
  box-shadow:0 3px 1px -2px rgb(0 0 0 / 20%),0 2px 2px 0 rgb(0 0 0 / 14%),0 1px 5px 0 rgb(0 0 0 / 12%);
}
.ion-button.button-disabled{cursor:default;opacity:.5;pointer-events:none;box-shadow:none}
.ion-button .button-inner{display:flex;flex-flow:row nowrap;flex-shrink:0;align-items:center;justify-content:center;width:100%;height:100%}

/* ion-text color="danger" → --ion-color-danger #f04141
   旧版样式表里没给 .danger-tip 定义规则，所以 p 保持浏览器默认 margin */
.ion-text-danger{color:var(--ion-color-danger,#f04141)}

.modal-notice{margin:10px 0 0;color:#999;font-size:.8rem;text-align:center}
</style>
